import { getEventTimestamp, processEvent } from './sessionHelper.js';
import { getSessionTypeColor } from './timelineHelper.js';
import { isAlertSessionType } from './sessionTypes.js';

/** Recherche binaire équivalente à bisect_right. */
export function bisectRight(timestamps, target) {
    let low = 0;
    let high = timestamps.length;
    while (low < high) {
        const mid = (low + high) >>> 1;
        if (timestamps[mid] <= target) low = mid + 1;
        else high = mid;
    }
    return low;
}

function cloneValue(value) {
    if (Array.isArray(value)) return value.map(cloneValue);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, cloneValue(child)]));
    }
    return value;
}

const bool = value => value === true || value === 'true';
const number = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};
const firstDefined = (...values) => values.find(value => value !== undefined && value !== null);

/**
 * Cache événementiel de la session.
 *
 * - l'état initial est le snapshot à t=0 ;
 * - chaque snapshot est obtenu par le même processEvent ;
 * - les événements sont ordonnés par leur timestamp métier ;
 * - un événement de rattrapage arrivé en retard reconstruit la suite du cache ;
 * - timeline, marqueur et tracé lisent donc la même succession d'états.
 */
export class SessionTimelineCache {
    constructor(initialState = null) {
        this.nextSequence = 0;
        this.reset(initialState);
    }

    reset(initialState = null) {
        this.nextSequence = 0;
        this.initialState = initialState ? cloneValue(initialState) : null;
        this.initialSnapshot = this._createInitialSnapshot(initialState);
        this.originTimestamp = this.initialSnapshot.timestamp;
        this.entries = [];
        this.events = [];
        this.eventTimestamps = [];
        this.snapshots = [];
        this.posEvents = [];
        this.posTimestamps = [];
    }

    _createInitialSnapshot(init) {
        const det = init?.detectionOptions || {};
        const startTs = number(firstDefined(init?.startTime, init?.timestamp), 0);
        const sessionType = init?.sessionType || null;
        let currentLocation = null;

        if (init?.currentLocation) {
            const lat = Number(firstDefined(init.currentLocation.latitude, init.currentLocation.lat));
            const lng = Number(firstDefined(init.currentLocation.longitude, init.currentLocation.lng));
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                currentLocation = {
                    ...cloneValue(init.currentLocation),
                    lat,
                    lng,
                    latitude: lat,
                    longitude: lng,
                    timestamp: startTs,
                    sourceTimestamp: number(init.currentLocation.timestamp, startTs),
                    addressName: init.currentLocation.addressName || init.destinationAddress || null,
                    sessionType,
                    isKeyEvent: false,
                };
            }
        }

        return {
            timestamp: startTs,
            sessionType,
            trigger: init?.trigger || null,
            appType: init?.appType || null,
            activityType: init?.activityType || null,
            shareType: init?.shareType || null,
            travelMode: init?.selectedTravelMode || init?.travelMode || 'WALK',
            isCorridorEnabled: bool(init?.isCorridorEnabled),
            corridorSizeMeters: number(init?.corridorSizeMeters, 25),
            corridorZone: cloneValue(init?.corridorZone || init?.corridor || []),
            // Trous du corridor (intérieur d'une boucle) : rendu en anneau.
            corridorHoles: cloneValue(init?.corridorHoles || []),
            trailApproachPoints: [],
            meetingZoneName: null,
            meetingZonePoints: [],
            routePoints: cloneValue(init?.routePoints || init?.itinerary || []),
            stopDetectionDurationMs: number(firstDefined(det.stopDetectionDurationMs, init?.stopDetectionDurationMs), 120000),
            isStopDetectionEnabled: bool(firstDefined(det.isStopDetectionEnabled, init?.isStopDetectionEnabled)),
            isStopped: bool(firstDefined(det.isStopped, init?.isStopped)),
            stopTimerValue: number(firstDefined(det.stopTimerValue, init?.stopTimerValue), 0),
            audio: bool(init?.audio),
            video: bool(init?.video),
            isAccidentDetectionEnabled: bool(firstDefined(det.isAccidentDetectionEnabled, init?.isAccidentDetectionEnabled)),
            isShakeDetectionEnabled: bool(firstDefined(det.isShakeDetectionEnabled, init?.isShakeDetectionEnabled)),
            isShortcutDetectionEnabled: bool(firstDefined(det.isShortcutDetectionEnabled, init?.isShortcutDetectionEnabled)),
            shortcutDetectionMode: String(firstDefined(det.shortcutDetectionMode, init?.shortcutDetectionMode, 'POWER')),
            currentLocation,
            victimRoute: [],
            keyEvents: [],
            responders: {},
            posEventsCount: 0,
            isEnded: false,
            status: 'ACTIVE',
        };
    }

    _entry(event) {
        const fallback = this.eventTimestamps[this.eventTimestamps.length - 1] || this.originTimestamp;
        return {
            event,
            timestamp: getEventTimestamp(event, fallback),
            sequence: this.nextSequence++,
        };
    }

    _appendEntry(entry) {
        const previous = this.snapshots[this.snapshots.length - 1] || this.initialSnapshot;
        const next = cloneValue(previous);
        // Le tracé est indexé séparément dans posEvents. Ne pas le recopier
        // dans chaque snapshot évite une consommation mémoire quadratique.
        next.victimRoute = [];
        next.keyEvents = [];
        next.timestamp = entry.timestamp;
        const change = processEvent(entry.event, next, true);
        const position = change?.position ? cloneValue(change.position) : null;

        if (position) {
            position.timestamp = entry.timestamp;
            position.sessionType = position.sessionType || next.sessionType;
            this.posEvents.push(position);
            this.posTimestamps.push(entry.timestamp);
            next.currentLocation = position;
        }

        next.victimRoute = [];
        next.keyEvents = [];
        next.posEventsCount = this.posEvents.length;
        this.events.push(entry.event);
        this.eventTimestamps.push(entry.timestamp);
        this.snapshots.push(next);
        return next;
    }

    _rebuild() {
        this.events = [];
        this.eventTimestamps = [];
        this.snapshots = [];
        this.posEvents = [];
        this.posTimestamps = [];
        this.entries.forEach(entry => this._appendEntry(entry));
    }

    /** Charge un historique complet en une seule reconstruction O(n log n). */
    replaceEvents(events) {
        this.entries = (events || []).map(event => this._entry(event));
        this.entries.sort((left, right) => left.timestamp - right.timestamp || left.sequence - right.sequence);
        if (!this.originTimestamp && this.entries.length > 0) {
            this.originTimestamp = this.entries[0].timestamp;
            this.initialSnapshot.timestamp = this.originTimestamp;
            if (this.initialSnapshot.currentLocation) {
                this.initialSnapshot.currentLocation.timestamp = this.originTimestamp;
            }
        }
        this._rebuild();
    }

    /**
     * Ajoute un événement live. Le chemin courant reste O(1). Un événement de
     * rattrapage antérieur est inséré puis le cache est reconstruit en O(n).
     */
    addEvent(event) {
        const entry = this._entry(event);
        const last = this.entries[this.entries.length - 1];
        if (!last || entry.timestamp >= last.timestamp) {
            this.entries.push(entry);
            return this._appendEntry(entry);
        }

        const insertAt = bisectRight(this.entries.map(item => item.timestamp), entry.timestamp);
        this.entries.splice(insertAt, 0, entry);
        this._rebuild();
        return this.snapshots[insertAt];
    }

    /** État exact après tous les événements dont timestamp <= targetTs. */
    getStateAtTimestamp(targetTs) {
        const countEvents = bisectRight(this.eventTimestamps, targetTs);
        const snapshot = countEvents > 0 ? this.snapshots[countEvents - 1] : this.initialSnapshot;
        const countPositions = bisectRight(this.posTimestamps, targetTs);
        const positions = this.posEvents.slice(0, countPositions);
        const currentLocation = countPositions > 0
            ? positions[countPositions - 1]
            : this.initialSnapshot.currentLocation;

        return {
            snapshot,
            positions,
            currentLocation,
            eventCount: countEvents,
            positionCount: countPositions,
            stateKey: countEvents > 0
                ? `event:${countEvents}:${this.eventTimestamps[countEvents - 1]}`
                : 'initial',
        };
    }

    getStateAtElapsed(secondsSinceStart) {
        if (Number(secondsSinceStart) <= 0) {
            return {
                // Aucun appelant ne peut modifier le point zéro canonique.
                snapshot: cloneValue(this.initialSnapshot),
                positions: [],
                currentLocation: cloneValue(this.initialSnapshot.currentLocation),
                eventCount: 0,
                positionCount: 0,
                stateKey: 'initial',
            };
        }
        return this.getStateAtTimestamp(this.originTimestamp + Math.max(0, secondsSinceStart) * 1000);
    }

    getLatestState() {
        const snapshot = this.snapshots[this.snapshots.length - 1] || this.initialSnapshot;
        const currentLocation = this.posEvents[this.posEvents.length - 1] || this.initialSnapshot.currentLocation;
        return { snapshot, positions: this.posEvents, currentLocation };
    }

    /**
     * Produit les segments depuis les snapshots du cache. Aucun reparsing
     * parallèle : la timeline et le marqueur utilisent exactement sessionType.
     */
    getTimelineData() {
        const minTs = this.originTimestamp || this.eventTimestamps[0] || 0;
        const maxTs = this.eventTimestamps[this.eventTimestamps.length - 1] || minTs;
        const defaultColor = getSessionTypeColor(this.initialSnapshot.sessionType);
        const segments = [{ startTs: minTs, endTs: maxTs, color: defaultColor }];
        let currentColor = defaultColor;
        let previous = this.initialSnapshot;
        const hasAlertMedia = snapshot => isAlertSessionType(snapshot?.sessionType)
            && Boolean(snapshot?.audio || snapshot?.video);
        let mediaStartTs = hasAlertMedia(previous) ? minTs : null;

        this.snapshots.forEach((snapshot, index) => {
            const timestamp = this.eventTimestamps[index];
            const color = getSessionTypeColor(snapshot.sessionType);
            if (color !== currentColor) {
                segments[segments.length - 1].endTs = timestamp;
                segments.push({ startTs: timestamp, endTs: maxTs, color });
                currentColor = color;
            }
            if (mediaStartTs === null && !hasAlertMedia(previous) && hasAlertMedia(snapshot)) {
                mediaStartTs = timestamp;
            }
            previous = snapshot;
        });

        return { segments, mediaStartTs, defaultColor, minTs, maxTs };
    }
}
